import { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmt, XFMR_IMPEDANCES, AFC_CONDUCTORS, AIC_RATINGS } from "./electrical-helpers";
import { readSettings } from "@/hooks/use-settings";
import type { useJobLog } from "@/hooks/use-job-log";

type SaveFn = (entry: Parameters<ReturnType<typeof useJobLog>["addEntry"]>[0]) => void;
const defaults = readSettings();

export const FaultCurrentCalc = ({ onSave }: { onSave?: SaveFn }) => {
  const [xfmrKva, setXfmrKva] = useState("75");
  const [primaryV, setPrimaryV] = useState(defaults.voltage);
  const [secondaryV, setSecondaryV] = useState("208");
  const [phases, setPhases] = useState<string>(defaults.phases);
  const [impedancePreset, setImpedancePreset] = useState("2.0");
  const [customZ, setCustomZ] = useState("");
  const [conductorIdx, setConductorIdx] = useState("9");
  const [conductorLength, setConductorLength] = useState("");
  const [parallelSets, setParallelSets] = useState("1");
  const [utilityMva, setUtilityMva] = useState("");

  const kva = parseFloat(xfmrKva);
  const secV = parseFloat(secondaryV);
  const ph = parseInt(phases);
  const phaseDivisor = ph === 3 ? 1.732 : 1;
  const zPct = impedancePreset === "custom" ? (parseFloat(customZ) || 0) : (XFMR_IMPEDANCES.find((x) => x.value === impedancePreset)?.z ?? 0);
  const condLength = parseFloat(conductorLength) || 0;
  const nParallel = Math.max(1, parseInt(parallelSets) || 1);
  const conductor = AFC_CONDUCTORS[parseInt(conductorIdx)] ?? AFC_CONDUCTORS[0];
  const utilMva = parseFloat(utilityMva) || 0;

  const secFla = kva > 0 && secV > 0 ? (kva * 1000) / (secV * phaseDivisor) : 0;
  const iscXfmr = zPct > 0 ? secFla / (zPct / 100) : 0;

  let iscWithUtility = iscXfmr;
  if (utilMva > 0 && kva > 0) {
    const zUtilPu = kva / (utilMva * 1000);
    const zTotalPu = zPct / 100 + zUtilPu;
    iscWithUtility = zTotalPu > 0 ? secFla / zTotalPu : 0;
  }

  const fSimple = condLength > 0
    ? (iscWithUtility * conductor.z * condLength * phaseDivisor) / (secV * 1000 * nParallel)
    : 0;
  const iscDownstream = condLength > 0 ? iscWithUtility / (1 + fSimple) : iscWithUtility;
  const requiredAic = AIC_RATINGS.find(a => a >= Math.round(iscDownstream)) ?? Math.round(iscDownstream);
  const hasInput = kva > 0 && secV > 0 && zPct > 0;

  return (
    <div className="space-y-3">
      <p className="text-[11px] text-muted-foreground leading-relaxed">Available fault current calculator — Point-to-point method per IEEE 141. Calculates AFC at transformer secondary and downstream panel.</p>

      <div className="grid grid-cols-2 gap-3">
        <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Transformer (kVA)</label><Input type="number" inputMode="decimal" value={xfmrKva} onChange={e => setXfmrKva(e.target.value)} placeholder="75" className="bg-secondary border-border font-mono-data text-lg" /></div>
        <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Secondary V</label><Select value={secondaryV} onValueChange={setSecondaryV}><SelectTrigger className="bg-secondary border-border font-mono-data"><SelectValue /></SelectTrigger><SelectContent>{["120","208","240","277","480"].map(v => <SelectItem key={v} value={v}>{v}V</SelectItem>)}</SelectContent></Select></div>
        <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Phases</label><Select value={phases} onValueChange={setPhases}><SelectTrigger className="bg-secondary border-border font-mono-data"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="1">1Φ</SelectItem><SelectItem value="3">3Φ</SelectItem></SelectContent></Select></div>
        <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Impedance (%Z)</label><Select value={impedancePreset} onValueChange={setImpedancePreset}><SelectTrigger className="bg-secondary border-border font-mono-data"><SelectValue /></SelectTrigger><SelectContent>{XFMR_IMPEDANCES.map(x => <SelectItem key={x.value} value={x.value}>{x.label}{x.z > 0 ? ` (${x.z}%)` : ""}</SelectItem>)}</SelectContent></Select></div>
      </div>

      {impedancePreset === "custom" && (
        <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Custom %Z</label><Input type="number" inputMode="decimal" value={customZ} onChange={e => setCustomZ(e.target.value)} placeholder="e.g. 3.5" className="bg-secondary border-border font-mono-data text-lg" /></div>
      )}

      <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Utility Available (MVA) — optional</label><Input type="number" inputMode="decimal" value={utilityMva} onChange={e => setUtilityMva(e.target.value)} placeholder="Infinite bus if blank" className="bg-secondary border-border font-mono-data text-lg" /></div>

      <h3 className="text-xs text-muted-foreground font-semibold uppercase tracking-wider pt-1">Downstream Conductor (optional)</h3>
      <div className="grid grid-cols-2 gap-3">
        <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Conductor</label><Select value={conductorIdx} onValueChange={setConductorIdx}><SelectTrigger className="bg-secondary border-border font-mono-data"><SelectValue /></SelectTrigger><SelectContent>{AFC_CONDUCTORS.map((c, i) => <SelectItem key={i} value={String(i)}>{c.label}</SelectItem>)}</SelectContent></Select></div>
        <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Length (ft)</label><Input type="number" inputMode="numeric" value={conductorLength} onChange={e => setConductorLength(e.target.value)} placeholder="One-way" className="bg-secondary border-border font-mono-data text-lg" /></div>
        <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Parallel Sets</label><Select value={parallelSets} onValueChange={setParallelSets}><SelectTrigger className="bg-secondary border-border font-mono-data"><SelectValue /></SelectTrigger><SelectContent>{["1","2","3","4"].map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent></Select></div>
      </div>

      {hasInput && (
        <div className="space-y-2 animate-fade-in">
          <div className="trade-card border-primary/40 bg-primary/5">
            <div className="flex justify-between items-center"><span className="text-sm text-muted-foreground">AFC at Transformer</span><span className="font-mono-data text-xl text-primary font-bold">{Math.round(iscWithUtility).toLocaleString()} A</span></div>
            <p className="text-[10px] text-muted-foreground mt-1">{kva} kVA, {secV}V {ph}Φ, {zPct}% Z{utilMva > 0 ? ` · ${utilMva} MVA utility` : " · infinite bus"}</p>
          </div>

          {condLength > 0 && (
            <div className="trade-card border-safety/40 bg-safety/5">
              <div className="flex justify-between items-center"><span className="text-sm text-muted-foreground">AFC at Panel ({condLength}ft)</span><span className="font-mono-data text-xl text-safety font-bold">{Math.round(iscDownstream).toLocaleString()} A</span></div>
              <p className="text-[10px] text-muted-foreground mt-1">{conductor.label} × {nParallel} set{nParallel > 1 ? "s" : ""} · {condLength}ft · f = {fmt(fSimple, 4)}</p>
            </div>
          )}

          <div className="trade-card border-accent/40 bg-accent/5">
            <div className="flex justify-between items-center"><span className="text-sm text-muted-foreground">Min Equipment AIC Rating</span><span className="font-mono-data text-lg font-bold text-foreground">{(requiredAic / 1000).toFixed(0)}k AIC</span></div>
          </div>

          <div className="trade-card border-muted/40">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Standard AIC Ratings</h3>
            <div className="flex flex-wrap gap-1.5">
              {AIC_RATINGS.map((aic) => (
                <span key={aic} className={`px-2 py-1 rounded text-[10px] font-mono-data border ${aic === requiredAic ? "bg-primary/15 border-primary/40 text-primary font-bold" : aic < Math.round(iscDownstream) ? "bg-destructive/10 border-destructive/30 text-destructive line-through" : "bg-card border-border text-muted-foreground"}`}>{(aic / 1000)}k</span>
              ))}
            </div>
          </div>
        </div>
      )}

      {hasInput && onSave && (
        <button type="button" onClick={() => {
          onSave({
            calcType: "fault-current", label: `AFC ${Math.round(iscDownstream).toLocaleString()}A — ${kva}kVA ${secV}V`,
            inputs: { transformer: `${kva} kVA`, secondary: `${secV}V`, phases: `${ph}Φ`, impedance: `${zPct}%` },
            results: [
              { label: "AFC at Transformer", value: Math.round(iscWithUtility).toLocaleString(), unit: "A" },
              ...(condLength > 0 ? [{ label: `AFC at Panel (${condLength}ft)`, value: Math.round(iscDownstream).toLocaleString(), unit: "A" }] : []),
              { label: "Min AIC Rating", value: `${(requiredAic / 1000).toFixed(0)}k`, unit: "AIC" },
            ],
            note: "",
          });
          toast.success("Saved to Job Log");
        }} className="w-full h-9 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors">💾 Save to Job Log</button>
      )}

      <p className="text-[11px] text-muted-foreground leading-relaxed mt-2">⚠️ Point-to-point method per IEEE 141. Uses infinite bus assumption unless utility MVA is specified. Always verify with an engineer.</p>
    </div>
  );
};
